import {
  LOGIN_POPUP_URL_PARAM,
  LOGIN_POPUP_URL_STATE,
  NEXT_PUBLIC_APOLLO_247_URL,
  NEXT_PUBLIC_PHARMACY_BASE_URL,
} from "@/libs/constants/auth";

export const clientRoutes = {
  oneTapConsultLanding: () => `/one-tap-online-doctor-consultation`,
  welcome: () => "/",
  doctorDetails: (doctorName: string, doctorId: string) => `/doctors/${doctorName}-${doctorId}`,
  specialties: (specialty: string) => `/specialties/${specialty}`,
  appointments: () => "/appointments",
  medicines: () => `/`,
  insurance: () => `/insurance`,
  medicineSearch: () => `/medicine-search`,
  orderStatusInfo: (transactionId: string, orderStatus: string) => `/order-status/${transactionId}/${orderStatus}`,
  healthRecords: (recordType: string) => {
    if (recordType === "") {
      return "/health-records";
    }

    if (recordType === "guest") {
      return "/guest-health-records";
    }

    return `/health-records/${recordType}`;
  },
  medicinesCart: () => "/medicines-cart",
  placePrescriptionOrder: () => "/upload-prescription/place-order",
  chatRoom: (appointmentId: string, doctorId: string) =>
    `${NEXT_PUBLIC_APOLLO_247_URL}/chat-room/${appointmentId}/${doctorId}`,
  myAccount: () => "/my-account",
  yourOrders: () => "/orders",
  orderDetails: (orderId: number | string) => `/orders/${encodeURIComponent(orderId)}`,
  medicineAllBrands: () => "/brands",
  searchByMedicine: (searchMedicineType: string, searchText: string) =>
    `/${encodeURIComponent(searchMedicineType)}/${encodeURIComponent(searchText)}`,
  searchKickoff: () => "/search-medicines",
  medicineDetails: (sku: string) => `/medicine/${encodeURIComponent(sku)}`,
  medicineHindiDetails: (sku: string) => `/hi//medicine/${encodeURIComponent(sku)}`,
  medicineDetailsOTC: (sku: string) => `/otc/${encodeURIComponent(sku)}`,
  medicineDetailsHindiOTC: (sku: string) => `/hi/otc/${encodeURIComponent(sku)}`,
  medicineListing: () => "/medicine",
  medicineOTCListing: () => "/otc",
  addressBook: () => "/address-book",
  tests: () => "/lab-tests",
  testDetails: (itemId: string) => `/lab-tests/${itemId}`,
  termsConditions: () => "/terms",
  privacyPolicy: () => "/privacy-policy",
  FAQ: () => "/faq",
  knowledgeBaseLanding: () => "/blog",
  kavachLanding: () => "/covid19/kavach",
  needHelp: () => "/needHelp",
  myPayments: () => "/my-payments",
  payMedicineJuspay: (orderId: string) => `/pay/${orderId}`,
  prescriptionReview: () => "/prescription-review",
  specialityListing: () => "/specialties",
  medicinePrescription: () => "/medicine-prescription",
  partnersHdfc: () => "/partners/hdfc",
  myMembership: () => "/my-membership",
  membershipPlanDetail: () => "/partners/hdfc-membership-plan-detail",
  dietetics: () => "specialties/dietetics",
  sitemap: (sitemap: string) => `/static/${sitemap}`,
  deliveryOptions: () => "/delivery-options",
  childSitemap: (sitemap: string, pageNo: string) =>
    Number(pageNo) === 1 ? `/static/${sitemap}` : `/static/${sitemap}?page=${pageNo}`,
  oneApolloMembership: () => "/oneapollo-membership",
  voucherBalance: () => "/voucher-balance",
  apolloProHealth: () => "/apollo-pro-health",
  careLanding: () => "/circle-landing",
  careFirst: () => "/landing/care-first",

  consultLanding: () => "/consult-landing",
  revampedUploadPrescription: () => "/upload-prescription",
  careMembership: () => `/circle-landing`,
  payCare: () => "/pay-care",
  storeLocator: () => "/medical-stores",
  storeDetails: (locality: string, city: string, storeId: string) =>
    `/medical-stores/apollo-pharmacy-${locality}-${city}-${storeId}`,
  storeLocatorWithCity: (city: string) => `/medical-stores/apollo-pharmacies-in-${city}-stcity`,
  signIn: (redirectUrl: string) =>
    `${window.location.pathname}?${LOGIN_POPUP_URL_PARAM}=${LOGIN_POPUP_URL_STATE}&continue=${redirectUrl}`,
  salt: (url: string) => `/salt/${encodeURIComponent(url)}`,
  manufacturer: (manufacturerName: string, manufacturer_id: string) =>
    `/manufacturer/${manufacturerName}-${manufacturer_id}`,
  manufacturerDirectory: () => `/manufacturers`,
  buyAgainProducts: () => "/buy-again-products",
  specialOffers: () => `/special-offers`,
  transactionSummary: (transactionId: string) => `/transaction-summary/${transactionId}`,
  paginationWithLabelMedicine: (pageNo: number | string, label?: string): string => {
    const isFirstPage = Number(pageNo || 1) === 1;
    const isValidLabel = label && label !== "a";

    if (isFirstPage) {
      return isValidLabel ? `/medicine?label=${label}` : `/medicine`;
    }

    return `/medicine?page=${pageNo}${isValidLabel ? `&label=${label}` : ""}`;
  },
  paginationWithLabelOTC: (pageNo: number | string, label?: string): string => {
    const isFirstPage = Number(pageNo || 1) === 1;
    const isValidLabel = label && label !== "a";

    if (isFirstPage) {
      return isValidLabel ? `/otc?label=${label}` : `/otc`;
    }

    return `/otc?page=${pageNo}${isValidLabel ? `&label=${label}` : ""}`;
  },
  alphabetBasedRoutingMedicine: (label?: string) => `/medicine?label=${label}`,
  alphabetBasedRoutingOTC: (label?: string) => `/otc?label=${label}`,
  recommendedProducts: () => `/recommended-products`,
  returnPolicy: () => "/returns",
  returnRequest: (orderId: number | string) => `/return-request/${orderId}`,
  corporateLanding: (name: string) => `/corporate-membership/${name}`,
  fullBodyCheckup: (source?: string) =>
    `/lab-tests/apollo-full-body-checkup-basic?
     ${source?.length ? `?source=${source}` : ""}`,
  pharmacyNearMe: () => `/pharmacy-nearby`,
  apolloSuper6: () => `/apollo-super6-program`,
  reportIssue: (lat?: number, lng?: number) => `/reportissue?lat=${lat}&lng=${lng}`,
  careplixProfile: () => "/health-face-scan/user-profile",
  careplixVitals: () => "/health-face-scan/user-profile/vital-check",
  careplixScanInstructions: () => "/health-face-scan/scan-instructions",
  careplixFaceScan: () => "/health-face-scan/face-scan",
  careplixCameraPermission: () => "/health-face-scan/camera-permission-guide",
  pharmaHome: () => NEXT_PUBLIC_PHARMACY_BASE_URL || "https://www.apollopharmacy.in/",
  addressDetails: () => `/address-details`,
  sbiCreditCard: () => "/apollo-sbi-credit-card",
  cartPrescription: () => "/cart-prescription-review",
  viewAllReview: (sku: string) => `/reviews/${sku}`,
};
